// src/components/sidebarcart/CartSummary.js
import React, { useCallback, useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Space, Typography, InputNumber, Tag, Button, Divider, Card, Alert } from 'antd';
import { FileTextOutlined, FileExcelOutlined, ReloadOutlined, LoadingOutlined, CheckCircleOutlined, WarningOutlined, SyncOutlined, SafetyOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import CartContext from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import CartItemList from './CartItemList';

const { Title, Text } = Typography;

function CartSummary({
    cartDetails,
    subTotalPrice,
    totalAfterDiscount,
    roundedTotal, // GIULIANO: Total redondeado al multiplo de step (o null)
    discountPercentage,
    setDiscountPercentage,
    clearCart,
    handleGenerateInvoice,
    handleExportToExcel,
    handleOpenPrintPage,
    isLoadingExport,
    isLoadingInvoice,
    isLoadingCart,
    getDiscountBreakdown,
    calculateCart,
    onRemoveItem,
    onUpdateQuantity,
    onUpdateSplitter,
    isAlreadyInvoiced = false,
    lastInvoiceNumber = null,
}) {
    // Get dual calculation state from context
    const { dualCalculation } = useContext(CartContext) || {};
    const { user } = useAuth();
    const isCreditCardMarkupEnabled = !!user?.creditCardMarkupEnabled;
    
    // Local state for discount input to prevent focus loss during typing
    const [localDiscount, setLocalDiscount] = useState(discountPercentage || 0);
    const [debounceTimer, setDebounceTimer] = useState(null);
    
    // Sync local state when prop changes (from outside, not from typing)
    useEffect(() => {
        setLocalDiscount(discountPercentage || 0);
    }, [discountPercentage]);
    
    const formatPrice = useCallback((price) => {
        if (typeof price !== 'number' || isNaN(price)) {
            return 'N/A';
        }
        return `$ ${price.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }, []);
    
    // Get correct discount breakdown
    const breakdown = typeof getDiscountBreakdown === 'function' 
        ? getDiscountBreakdown()
        : {
            discountAmount: subTotalPrice - totalAfterDiscount,
            finalTotal: totalAfterDiscount,
            calculationSource: 'fallback',
            isCalculating: false
        };
    
    const { isCalculating = false, error = null } = breakdown;

    // Handle local input change with debounce (auto-recalculates after 500ms)
    const handleLocalDiscountChange = useCallback((value) => {
        const newValue = value || 0;
        setLocalDiscount(newValue);
        
        // Clear existing timer
        if (debounceTimer) {
            clearTimeout(debounceTimer);
        }
        
        // Set new timer to update parent after 500ms of no typing
        const timer = setTimeout(() => {
            setDiscountPercentage(newValue);
        }, 500);
        
        setDebounceTimer(timer);
    }, [debounceTimer, setDiscountPercentage]);
    
    // Commit discount immediately on blur or enter
    const handleDiscountCommit = useCallback(() => {
        // Clear debounce timer
        if (debounceTimer) {
            clearTimeout(debounceTimer);
            setDebounceTimer(null);
        }
        // Immediately update if different
        if (localDiscount !== discountPercentage) {
            setDiscountPercentage(localDiscount);
        }
    }, [localDiscount, discountPercentage, setDiscountPercentage, debounceTimer]);

    // Cleanup debounce timer on unmount
    useEffect(() => {
        return () => {
            if (debounceTimer) {
                clearTimeout(debounceTimer);
            }
        };
    }, [debounceTimer]);

    const disableButtons = cartDetails.length === 0 || isLoadingExport || isLoadingCart || isLoadingInvoice;

    return (
        <Card 
            style={{ 
                marginTop: 'auto',
                marginBottom: '16px',
                marginRight: '8px',
                marginLeft: '8px',
                background: '#ffffff',
                border: '1px solid #f0f0f0',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                height: '85vh',
                display: 'flex',
                flexDirection: 'column'
            }}
            bodyStyle={{ padding: '20px', display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}
        >
            {/* Header with Validation Badge */}
            <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Title level={4} style={{ color: '#262626', margin: 0, fontWeight: 600 }}>
                    Resumen del Carrito
                </Title>
                {/* Show volume pricing warning badge if detected */}
                {breakdown?.volumePricingWarning ? (
                    <Tag 
                        icon={<ExclamationCircleOutlined />}
                        color="warning"
                        style={{ fontSize: '11px', fontWeight: 500, padding: '2px 8px', margin: 0 }}
                    >
                        ⚡ Backend no soporta precios por volumen
                    </Tag>
                ) : dualCalculation && dualCalculation.validationBadge?.show ? (
                    <Tag 
                        icon={
                            dualCalculation.isValidating ? <SyncOutlined spin /> :
                            dualCalculation.mismatchDetected ? <ExclamationCircleOutlined /> :
                            dualCalculation.isValidated ? <SafetyOutlined /> :
                            <LoadingOutlined />
                        }
                        color={dualCalculation.validationBadge.color}
                        style={{ fontSize: '11px', fontWeight: 500, padding: '2px 8px', margin: 0 }}
                    >
                        {dualCalculation.isValidated ? 'Validado por servidor' : dualCalculation.validationBadge.text}
                    </Tag>
                ) : (
                    <>
                        {!error && (
                            <Tag 
                                icon={isCalculating ? <LoadingOutlined /> : <CheckCircleOutlined />}
                                color={isCalculating ? "processing" : "success"}
                                style={{ fontSize: '11px', fontWeight: 500, padding: '2px 8px', margin: 0 }}
                            >
                                {isCalculating ? "Calculando..." : "Validado por servidor"}
                            </Tag>
                        )}
                    </>
                )}
            </div>

            <Divider style={{ margin: '12px 0' }} />

            {/* Cart Items Table */}
            {cartDetails.length > 0 && (
                <div style={{ flex: 1, marginBottom: '16px', overflow: 'auto', minHeight: 0 }}>
                    <CartItemList
                        cartDetails={cartDetails}
                        onRemoveItem={onRemoveItem}
                        onUpdateQuantity={onUpdateQuantity}
                        onUpdateSplitter={onUpdateSplitter}
                        isLoadingCart={isLoadingCart}
                    />
                </div>
            )}
            
            {/* Totals Section - AFIP Compliant */}
            <div style={{ 
                width: '100%',
                marginBottom: '16px',
                padding: '16px',
                background: '#fafafa',
                borderRadius: '6px',
                border: '1px solid #f0f0f0'
            }}>
                {/* Subtotal (Sin IVA) - AFIP Compliant */}
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '10px'
                }}>
                    <Text style={{ color: '#595959', fontSize: '14px' }}>
                        Subtotal (Sin IVA):
                    </Text>
                    <Text style={{ 
                        color: '#262626', 
                        fontSize: '15px',
                        fontWeight: 600,
                        opacity: isCalculating ? 0.6 : 1
                    }}>
                        {formatPrice(breakdown.subtotalWithoutIVA || (subTotalPrice / 1.21))}
                    </Text>
                </div>
                
                {/* Discount Input with Amount - AFIP Compliant */}
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '12px'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Text style={{ color: '#595959', fontSize: '14px' }}>
                            Descuento:
                        </Text>
                        <Space size={4}>
                            <InputNumber
                                min={0}
                                max={100}
                                value={localDiscount}
                                onChange={handleLocalDiscountChange}
                                onBlur={handleDiscountCommit}
                                onPressEnter={handleDiscountCommit}
                                disabled={isCalculating}
                                size="small"
                                style={{ width: '70px' }}
                                formatter={value => `${value}`}
                                parser={value => value.replace(/[^\d]/g, '')}
                            />
                            <Text style={{ color: '#262626', fontSize: '14px', fontWeight: 600 }}>%</Text>
                        </Space>
                    </div>
                    <Text style={{ 
                        color: '#ff4d4f', 
                        fontSize: '15px',
                        fontWeight: 600,
                        opacity: isCalculating ? 0.6 : 1
                    }}>
                        {discountPercentage > 0 ? `-${formatPrice(breakdown.discountAmount || 0)}` : '-'}
                    </Text>
                </div>

                {/* IVA (21%) - AFIP Compliant */}
                {breakdown.ivaAmount !== undefined && (
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '12px'
                    }}>
                        <Text style={{ color: '#595959', fontSize: '14px' }}>
                            IVA (21%):
                        </Text>
                        <Text style={{ 
                            color: '#262626', 
                            fontSize: '15px',
                            fontWeight: 600,
                            opacity: isCalculating ? 0.6 : 1
                        }}>
                            {formatPrice(breakdown.ivaAmount)}
                        </Text>
                    </div>
                )}

                <Divider style={{ margin: '10px 0' }} />
                
                {/* Total - AFIP Compliant */}
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <Text style={{ color: '#262626', fontSize: '15px', fontWeight: 600 }}>
                        Total:
                    </Text>
<Title
                        level={4}
                        style={{
                            color: '#1890ff',
                            margin: 0,
                            fontSize: '22px',
                            fontWeight: 700,
                            animation: isCalculating ? 'pulse 1.5s infinite' : 'none'
                        }}
                    >
                        {formatPrice(roundedTotal != null ? roundedTotal : (breakdown.finalTotal || totalAfterDiscount))}
                    </Title>
                </div>
            </div>

            {/* Action Buttons */}
            <Space direction="vertical" style={{ width: '100%' }} size={10}>

                {/* ⚠️ Already-invoiced warning banner */}
                {isAlreadyInvoiced && (
                    <Alert
                        type="warning"
                        showIcon
                        message={
                            <span style={{ fontSize: '13px', fontWeight: 500 }}>
                                Este carrito ya fue facturado
                                {lastInvoiceNumber ? (
                                    <> &mdash; N&deg;&nbsp;<strong>{lastInvoiceNumber}</strong></>
                                ) : null}.
                                {' '}Modificá el carrito para habilitar una nueva factura limpia.
                            </span>
                        }
                        style={{ borderRadius: '6px', padding: '8px 12px' }}
                    />
                )}

                <Button
                    type="primary"
                    icon={<FileTextOutlined />}
                    onClick={handleGenerateInvoice}
                    disabled={disableButtons}
                    loading={isLoadingInvoice}
                    size="large"
                    block
                    style={{
                        height: '48px',
                        fontSize: '15px',
                        fontWeight: 600,
                        borderRadius: '6px',
                        ...(isAlreadyInvoiced && {
                            backgroundColor: '#fa8c16',
                            borderColor: '#fa8c16',
                        }),
                    }}
                >
                    {isAlreadyInvoiced ? 'GENERAR FACTURA NUEVAMENTE' : 'GENERAR FACTURA'}
                </Button>

                {!isCreditCardMarkupEnabled && (
                <Button
                    icon={<FileExcelOutlined />}
                    onClick={handleExportToExcel}
                    disabled={disableButtons}
                    loading={isLoadingExport}
                    size="large"
                    block
                    style={{
                        backgroundColor: '#52c41a',
                        borderColor: '#52c41a',
                        color: '#fff',
                        height: '42px',
                        fontSize: '14px',
                        fontWeight: 500,
                        borderRadius: '6px'
                    }}
                >
                    Exportar Excel
                </Button>
                )}

                <Button
                    icon={<ReloadOutlined />}
                    onClick={clearCart}
                    disabled={disableButtons}
                    size="large"
                    block
                    style={{
                        height: '42px',
                        fontSize: '14px',
                        fontWeight: 500,
                        borderRadius: '6px',
                        backgroundColor: '#fffbe6',
                        borderColor: '#ffe58f',
                        color: '#d48806'
                    }}
                >
                    Nueva Cotización
                </Button>
            </Space>

            <style>{`
                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.6; }
                }
            `}</style>
        </Card>
    );
}

CartSummary.propTypes = {
    cartDetails: PropTypes.array.isRequired,
    subTotalPrice: PropTypes.number.isRequired,
    totalAfterDiscount: PropTypes.number.isRequired,
    roundedTotal: PropTypes.number, // GIULIANO: Total redondeado (o null si step=0)
    discountPercentage: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    setDiscountPercentage: PropTypes.func.isRequired,
    clearCart: PropTypes.func.isRequired,
    handleGenerateInvoice: PropTypes.func.isRequired,
    handleExportToExcel: PropTypes.func.isRequired,
    handleOpenPrintPage: PropTypes.func.isRequired,
    isLoadingExport: PropTypes.bool.isRequired,
    isLoadingInvoice: PropTypes.bool.isRequired,
    isLoadingCart: PropTypes.bool.isRequired,
    getDiscountBreakdown: PropTypes.func,
    calculateCart: PropTypes.func,
    onRemoveItem: PropTypes.func.isRequired,
    onUpdateQuantity: PropTypes.func.isRequired,
    isAlreadyInvoiced: PropTypes.bool,
    lastInvoiceNumber: PropTypes.string,
};

export default React.memo(CartSummary);
